import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X, Heart } from 'lucide-react';

const Header13 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template13' },
        { label: 'Shop', path: '/template13/shop' },
        { label: 'About', path: '/template13/about' },
        { label: 'Contact', path: '/template13/contact' },
        { label: 'Orders', path: '/template13/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&family=Mulish:wght@300;400;600&display=swap');
                .karigar-desktop-nav { display: none; }
                .karigar-mobile-toggle { display: flex; }
                @media (min-width: 1024px) {
                    .karigar-desktop-nav { display: flex; }
                    .karigar-mobile-toggle { display: none; }
                }
            `}</style>

            <header
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: scrolled ? 'rgba(251, 248, 244, 0.98)' : '#FBF8F4',
                    borderBottom: scrolled ? '1px solid #E7E0D6' : '1px solid transparent',
                    transition: 'all 0.3s ease',
                    boxShadow: scrolled ? '0 2px 12px rgba(0,0,0,0.06)' : 'none',
                }}
            >
                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 20px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '72px',
                    }}
                >
                    {/* Mobile Menu Toggle */}
                    <button
                        className="karigar-mobile-toggle"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '44px',
                            minHeight: '44px',
                        }}
                    >
                        {mobileMenuOpen
                            ? <X style={{ width: '22px', height: '22px', color: '#1C1917' }} />
                            : <Menu style={{ width: '22px', height: '22px', color: '#1C1917' }} />
                        }
                    </button>

                    {/* Brand */}
                    <h1
                        onClick={() => navigate('/template13')}
                        style={{
                            fontFamily: "'Merriweather', Georgia, serif",
                            fontSize: '24px',
                            fontWeight: 700,
                            color: '#C2410C',
                            cursor: 'pointer',
                            letterSpacing: '2px',
                            margin: 0,
                            textTransform: 'uppercase',
                        }}
                    >
                        KARIGAR
                    </h1>

                    {/* Desktop Nav */}
                    <nav className="karigar-desktop-nav" style={{ alignItems: 'center', gap: '32px' }}>
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                style={{
                                    fontFamily: "'Merriweather', Georgia, serif",
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    color: '#1C1917',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px 0',
                                    letterSpacing: '0.5px',
                                    transition: 'color 0.3s ease',
                                    position: 'relative',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = '#C2410C'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = '#1C1917'; }}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right Icons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <Search
                            style={{ width: '20px', height: '20px', color: '#1C1917', cursor: 'pointer', transition: 'color 0.3s' }}
                            className="hidden lg:block"
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#C2410C'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#1C1917'; }}
                        />
                        <Heart
                            style={{ width: '20px', height: '20px', color: '#1C1917', cursor: 'pointer', transition: 'color 0.3s' }}
                            className="hidden lg:block"
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#C2410C'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#1C1917'; }}
                        />
                        <div
                            onClick={() => navigate('/template13/cart')}
                            style={{ position: 'relative', cursor: 'pointer' }}
                        >
                            <ShoppingCart
                                style={{ width: '20px', height: '20px', color: '#1C1917', transition: 'color 0.3s' }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = '#C2410C'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = '#1C1917'; }}
                            />
                            {cartCount > 0 && (
                                <span style={{
                                    position: 'absolute', top: '-8px', right: '-8px',
                                    backgroundColor: '#C2410C', color: '#FFFFFF',
                                    fontSize: '10px', fontWeight: 600,
                                    width: '18px', height: '18px', borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontFamily: "'Mulish', sans-serif",
                                }}>
                                    {cartCount}
                                </span>
                            )}
                        </div>
                        <User
                            onClick={() => navigate('/template13/profile')}
                            style={{ width: '20px', height: '20px', color: '#1C1917', cursor: 'pointer', transition: 'color 0.3s' }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#C2410C'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#1C1917'; }}
                        />
                    </div>
                </div>

                {/* Mobile Menu Overlay */}
                {mobileMenuOpen && (
                    <div style={{
                        position: 'fixed', top: '72px', left: 0, right: 0, bottom: 0,
                        backgroundColor: '#FBF8F4', zIndex: 49,
                        overflowY: 'auto',
                    }}>
                        <nav style={{ padding: '24px 20px' }}>
                            {navLinks.map((link) => (
                                <button
                                    key={link.label}
                                    onClick={() => handleNavClick(link.path)}
                                    style={{
                                        display: 'block', width: '100%', textAlign: 'left',
                                        fontFamily: "'Merriweather', Georgia, serif",
                                        fontSize: '18px', fontWeight: 400, color: '#1C1917',
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        padding: '16px 0', borderBottom: '1px solid #E7E0D6',
                                    }}
                                >
                                    {link.label}
                                </button>
                            ))}
                            <div style={{ display: 'flex', gap: '20px', paddingTop: '24px' }}>
                                <Search style={{ width: '22px', height: '22px', color: '#1C1917', cursor: 'pointer' }} />
                                <Heart style={{ width: '22px', height: '22px', color: '#1C1917', cursor: 'pointer' }} />
                            </div>
                        </nav>
                    </div>
                )}
            </header>
        </>
    );
};

export default Header13;
